import {
  ComparatorContext,
  useCurrentStepIndex,
  useFields,
  useForm,
} from '@kizi/comparator-core';
import { Button } from '@kizi/core-components';
import React, { useCallback, useContext } from 'react';
import { CarPremiumContext } from '../../../context/car-premium-context';
import { scrollToTop } from '../../../utils/global';
import { usePeregrineEventHandler } from '../../../utils/helpers';

const formatDate = (dateStr, isOminimoProduct = false) => {
  if (!dateStr) {
    if (isOminimoProduct) {
      // For Ominimo, return today's date when no date is set
      // This ensures the "later than today" check fails appropriately
      const today = new Date();
      return today.toISOString().split('T')[0];
    } else {
      // For other providers, return tomorrow as before
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }
  }

  const [day, month, year] = dateStr.split('-');
  return `${year}-${month}-${day}`;
};

export const SubmitCarApplyButton = (props: any) => {
  const wizardStepIndex = useCurrentStepIndex() ?? '';
  const { send, state } = useForm(wizardStepIndex);
  const { api, getStateFromLocalStorage } = useContext(ComparatorContext);
  const { hasNewPremium, error } = useContext(CarPremiumContext);
  const handlePeregrineEvent = usePeregrineEventHandler(props, state, api);
  const { tempProduct } = useContext(CarPremiumContext);
  const persistedState = getStateFromLocalStorage();
  const parsedCurrentSelectedProduct = JSON.parse(
    persistedState?.context?.initialSteps?.[3].fields?.[3]?.value as string,
  );
  let canSubmit = true;
  const [isCbMember, isMember, startDate] = useFields([
    '*.is_cb_member',
    '*.isMember',
    '*.startdate',
  ]);

  if (isCbMember?.value === 'ja' && isMember?.value === 0) {
    canSubmit = false;
  }

  if (!send || !state) return null;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;

    if (tempProduct && parsedCurrentSelectedProduct?.provider?.id === '1139') {
      localStorage.setItem('ominimoProduct', JSON.stringify(tempProduct));
    }
    send('SUBMIT');
    if (state.value === 'completed') {
      scrollToTop();
    }
  }, [canSubmit, send, state.value]);

  const handleClick = useCallback(async () => {
    await handlePeregrineEvent();
    handleSubmit();
  }, [handlePeregrineEvent, handleSubmit]);

  const checkCarNewPremium = useCallback(async () => {
    const persistedState = getStateFromLocalStorage();
    if (persistedState === null) return;

    try {
      const currentSelectedProduct = JSON.parse(
        persistedState?.context?.initialSteps?.[3].fields?.[3]?.value as string,
      );

      // Check if it's an Ominimo product
      const isOminimoProduct = currentSelectedProduct?.provider?.id === '1139';

      const date = formatDate(startDate?.value as string, isOminimoProduct);

      const premiumChanged = await hasNewPremium(date, currentSelectedProduct);

      // If there's an error, log it and stop
      if (error) {
        console.log('Premium check error:', error);
        return;
      } else if (!premiumChanged) {
        handleClick();
      }
    } catch (e) {
      console.error('Error in checkCarNewPremium:', e);
    }
  }, [
    getStateFromLocalStorage,
    startDate?.value,
    hasNewPremium,
    error,
    handleClick,
  ]);

  const onClick = useCallback(() => {
    if (props?.isLastStep && canSubmit) {
      if (state.value !== 'completed') {
        send('SUBMIT');
        return;
      }

      return checkCarNewPremium();
    }
    return handleClick();
  }, [
    props?.isLastStep,
    startDate?.value,
    canSubmit,
    checkCarNewPremium,
    handleClick,
  ]);

  return (
    <Button onClick={onClick} {...props}>
      {props.children}
    </Button>
  );
};
